/**
 * OverdueSection Component
 *
 * Displays overdue scheduled transactions at the top of the Transactions page.
 * Shows items that are 2+ days past their due date.
 *
 * Features:
 * - Collapsible section
 * - Warning styling (red accent)
 * - Click to open actions menu (Pay, Skip, Ignore)
 */

import { useState, useCallback } from 'react';
import { useOverdue } from '@hooks/useOverdue';
import { Account } from '@services/db';
import { OverdueItem } from '@services/scheduledRunner';
import { OverdueItemMenu } from './OverdueItemMenu';
import { formatCurrency as formatCurrencyIntl, formatMonthDay } from '@/utils/format';
import { useLanguage } from '@/i18n';
import './OverdueSection.css';

// ============================================================
// Types
// ============================================================

interface OverdueSectionProps {
  /** Accounts map for displaying account names */
  accounts: Map<string, Account>;
}

// ============================================================
// Helpers
// ============================================================

// Central locale-aware formatters (utils/format.ts) - BUG-074 safe-currency
// fallback now lives there. Never inline Intl here.
function formatDueDate(date: Date): string {
  return formatMonthDay(date);
}

function formatAmount(amount: number, currency?: string): string {
  return formatCurrencyIntl(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// ============================================================
// Component
// ============================================================

export function OverdueSection({ accounts }: OverdueSectionProps) {
  const { t } = useLanguage();
  const { items, loading, count, pay, skip, skipAndRemove, dismiss, ignoreForMonth, ignorePermanently } = useOverdue();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OverdueItem | null>(null);

  // Handle item click
  const handleItemClick = useCallback((item: OverdueItem) => {
    setSelectedItem(item);
  }, []);

  // Handle menu close
  const handleMenuClose = useCallback(() => {
    setSelectedItem(null);
  }, []);

  // Handle pay action
  const handlePay = useCallback(async () => {
    if (!selectedItem) return;
    await pay(selectedItem.schedule.id, selectedItem.dueDate);
  }, [selectedItem, pay]);

  // Handle skip action
  const handleSkip = useCallback(async () => {
    if (!selectedItem) return;
    await skip(selectedItem.schedule.id, selectedItem.dueDate);
  }, [selectedItem, skip]);

  // Handle skip & remove action (skip this + delete recurring)
  const handleSkipAndRemove = useCallback(async () => {
    if (!selectedItem) return;
    await skipAndRemove(selectedItem.schedule.id, selectedItem.dueDate);
  }, [selectedItem, skipAndRemove]);

  // Handle dismiss action (tracks dismissCount, shows escalation on 2nd dismiss)
  const handleDismiss = useCallback(async () => {
    if (!selectedItem) return { showEscalationPopup: false };
    return await dismiss(selectedItem.schedule.id);
  }, [selectedItem, dismiss]);

  // Handle ignore month action
  const handleIgnoreMonth = useCallback(async () => {
    if (!selectedItem) return;
    await ignoreForMonth(selectedItem.schedule.id);
  }, [selectedItem, ignoreForMonth]);

  // Handle ignore permanently action
  const handleIgnorePermanently = useCallback(async () => {
    if (!selectedItem) return;
    await ignorePermanently(selectedItem.schedule.id);
  }, [selectedItem, ignorePermanently]);

  // Don't show if no overdue items
  if (loading || count === 0) {
    return null;
  }

  return (
    <div className="overdue-section">
      {/* Header */}
      <div className="overdue-header">
        <div className="overdue-header-left">
          <span className="overdue-icon">!</span>
          <span className="overdue-label">{t('overdue.label')}</span>
          <span className="overdue-count">({count})</span>
        </div>
        <button
          className="overdue-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? t('transactions.upcoming.show') : t('transactions.upcoming.hide')}
        </button>
      </div>

      {/* Items */}
      {!isCollapsed && (
        <div className="overdue-card">
          {items.map((item) => {
            const account = accounts.get(item.schedule.accountId);

            return (
              <div
                key={`${item.schedule.id}-${item.dueDate.getTime()}`}
                className="overdue-item"
                onClick={() => handleItemClick(item)}
              >
                <div className="overdue-item-icon">
                  <span className="overdue-dot" />
                </div>
                <div className="overdue-item-info">
                  <span className="overdue-item-name">{item.schedule.description}</span>
                  <span className="overdue-item-meta">
                    {formatDueDate(item.dueDate)} - {t('overdue.days_overdue', { days: item.daysOverdue })}
                    {account && ` - ${account.name}`}
                  </span>
                </div>
                <div className="overdue-item-right">
                  <span className={`overdue-item-amount ${item.schedule.type === 'expense' ? 'expense' : 'income'}`}>
                    {item.schedule.type === 'expense' ? '-' : '+'}{formatAmount(item.schedule.amount, account?.currency)}
                  </span>
                  <span className="overdue-item-arrow">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Menu */}
      <OverdueItemMenu
        isOpen={!!selectedItem}
        schedule={selectedItem?.schedule || null}
        dueDate={selectedItem?.dueDate || null}
        daysOverdue={selectedItem?.daysOverdue || 0}
        currency={selectedItem ? accounts.get(selectedItem.schedule.accountId)?.currency : undefined}
        onClose={handleMenuClose}
        onPay={handlePay}
        onSkip={handleSkip}
        onSkipAndRemove={handleSkipAndRemove}
        onDismiss={handleDismiss}
        onIgnoreMonth={handleIgnoreMonth}
        onIgnorePermanently={handleIgnorePermanently}
      />
    </div>
  );
}

export default OverdueSection;
